import React, { useEffect, useState } from "react";
import { api } from "../lib/api";
import { useI18n } from "../context/I18nContext";
import { Reveal, SectionTitle } from "../components/shared";
import { Globe2, Sunrise, Utensils, BookOpen, Bed, Sun } from "lucide-react";

const schedule = [
  { time: "7:00 – 8:30", icon: Sunrise, title: "Warm Welcome & Free Play", desc: "Gentle arrivals, breakfast, and open exploration." },
  { time: "8:30 – 9:00", icon: Utensils, title: "Morning Snack & Circle", desc: "Songs, stories, and planning the day together." },
  { time: "9:00 – 10:30", icon: BookOpen, title: "Atelier & Project Time", desc: "Creative studios and small-group discovery." },
  { time: "10:30 – 11:30", icon: Sun, title: "Garden & Outdoor Play", desc: "Fresh air, movement, and nature exploration." },
  { time: "11:30 – 12:30", icon: Utensils, title: "Cooking & Lunch", desc: "Little chefs help prepare and share a healthy meal." },
  { time: "12:30 – 2:30", icon: Bed, title: "Rest & Quiet Time", desc: "Naps and calm activities to recharge." },
  { time: "2:30 – 6:00", icon: BookOpen, title: "Afternoon Adventures", desc: "Snack, projects, outdoor play, and cozy goodbyes." },
];

export default function About() {
  const { t } = useI18n();
  const [teachers, setTeachers] = useState([]);
  useEffect(() => { api.get("/teachers").then(({ data }) => setTeachers(data)).catch(() => {}); }, []);

  return (
    <div className="pt-28">
      <section className="max-w-7xl mx-auto px-6 py-12">
        <Reveal>
          <div className="text-coral font-semibold tracking-widest uppercase text-xs mb-3">About Ainalem</div>
          <h1 className="font-heading text-4xl sm:text-5xl lg:text-6xl font-extrabold text-navy tracking-tight max-w-3xl">A warm second home for your little one</h1>
          <p className="mt-5 text-lg text-navy/60 max-w-2xl leading-relaxed">Ainalem Family Childcare was founded on a simple belief: children thrive when they feel loved, seen, and free to explore. We blend the Reggio-Emilia philosophy with the intimacy of family childcare, caring for children ages {"6 weeks"} to 5 years.</p>
        </Reveal>
      </section>

      {/* Philosophy */}
      <section className="bg-white py-20">
        <div className="max-w-7xl mx-auto px-6 grid lg:grid-cols-2 gap-12 items-center">
          <Reveal><img src="https://images.unsplash.com/photo-1666710988451-ba4450498967?crop=entropy&cs=srgb&fm=jpg&q=85&w=1000" alt="Children creating art" className="rounded-3xl shadow-xl w-full aspect-[4/3] object-cover" /></Reveal>
          <Reveal delay={0.15}>
            <SectionTitle title={t.sections.philosophyTitle} />
            <p className="text-lg text-navy/70 leading-relaxed">The child is the protagonist of their own learning. We see teachers as researchers and guides, the environment as the "third teacher," and documentation as a way to make children's thinking visible. Art, nature, and food are our everyday languages of learning.</p>
          </Reveal>
        </div>
      </section>

      {/* Daily schedule */}
      <section className="max-w-4xl mx-auto px-6 py-20">
        <Reveal><SectionTitle eyebrow="Rhythm of the day" title={t.sections.scheduleTitle} center /></Reveal>
        <div className="relative">
          <div className="absolute left-[27px] top-2 bottom-2 w-px bg-mustard/40 hidden sm:block" />
          <div className="space-y-4">
            {schedule.map((s, i) => (
              <Reveal key={i} delay={i * 0.06}>
                <div className="flex gap-4 items-start" data-testid={`schedule-${i}`}>
                  <div className="h-14 w-14 rounded-2xl bg-navy flex items-center justify-center text-white shrink-0 z-10"><s.icon className="h-6 w-6" /></div>
                  <div className="bg-white rounded-2xl p-5 shadow-sm border border-black/5 flex-1">
                    <div className="text-xs font-semibold text-coral uppercase tracking-wider">{s.time}</div>
                    <div className="font-heading font-bold text-navy mt-0.5">{s.title}</div>
                    <div className="text-sm text-navy/60 mt-1">{s.desc}</div>
                  </div>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* Meals */}
      <section className="bg-coral text-white py-16">
        <div className="max-w-5xl mx-auto px-6 grid md:grid-cols-2 gap-8 items-center">
          <Reveal>
            <div className="h-12 w-12 rounded-2xl bg-white/20 flex items-center justify-center mb-4"><Utensils className="h-6 w-6" /></div>
            <h2 className="font-heading text-3xl font-bold">{t.sections.mealsTitle}</h2>
            <p className="mt-4 text-white/90 text-lg leading-relaxed">We serve fresh, whole-food meals and snacks every day — many featuring vegetables and herbs the children grow in our garden and help prepare in our cooking program. Menus accommodate allergies and dietary needs.</p>
          </Reveal>
          <Reveal delay={0.15}><img src="https://images.unsplash.com/photo-1758874960608-f0d7f38d9846?crop=entropy&cs=srgb&fm=jpg&q=85&w=900" alt="Cooking together" className="rounded-3xl shadow-xl w-full aspect-[4/3] object-cover" /></Reveal>
        </div>
      </section>

      {/* DEIB */}
      <section className="max-w-5xl mx-auto px-6 py-20 text-center">
        <Reveal>
          <div className="h-14 w-14 rounded-2xl bg-navy mx-auto flex items-center justify-center text-white mb-5"><Globe2 className="h-7 w-7" /></div>
          <h2 className="font-heading text-3xl sm:text-4xl font-bold text-navy">{t.sections.deibTitle}</h2>
          <p className="mt-4 text-lg text-navy/60 max-w-2xl mx-auto leading-relaxed">{t.sections.deibSub}</p>
          <div className="flex flex-wrap gap-3 justify-center mt-8">
            {["🇬🇧 English", "🇪🇸 Español", "🇮🇹 Italiano", "🇪🇷 ትግርኛ"].map((l) => (
              <span key={l} className="px-5 py-2.5 rounded-full bg-white shadow-sm border border-black/5 font-semibold text-navy">{l}</span>
            ))}
          </div>
        </Reveal>
      </section>
    </div>
  );
}
